import { PRIORITY, PROGRAM, PROGRAM_DEPARTMENT, PROGRAM_DIVISION, REQUEST, SCORING_RUBRIC, SINGULAR, USER_GROUP } from '../../static/keyWords.js';
import { trimValue } from '../reusable/reusableFunctions.js';

export const INTERNALLY_USED_WORDS = new Set([
  'id',
  'last name',
  'first name',
  'action',
  'edit',
  'email',
  'role',
  'access',
  'budget name',
  'budget year',
  'year',
  'account code',
  'account type',
  'itemid',
  'acctid',
  'acctcode',
  'duration type',
  'ongoing_cost',
  'onetime_cost',
  'total item cost',
  'number of items',
  'fund',
  'itemmeta1',
  'itemmeta2',
  'itemmeta3',
  'namemeta',
  'itemcomments',
  'servicetype',
  'program number',
  'comments',
  'proggroup',
  'progsubgroup',
  'progid',
  'object type',
  'total cost',
  'allocation',
  'item category 1',
  'item category 2',
  'allocationid',
  'allocated cost',
  'organizational levels',
  'category levels',
  'fte',
  'personnel',
  'nonpersonnel',
  'revenue',
  'final score',
  'final score option',
  'final comments',
  'dept score',
  'department score option',
  'resultid',
  'department comments'
]);
export const inputTypes = ['singular', 'plural'];
export const ALIAS = 'alias';
export const PLURAL_FORM_ALIAS = 'pluralFormAlias';
export const NAME_CONVENTION_EXPANSION_PANEL_WIDTH = '75vw';
export const transformObjectToArray = (nameConventions, target) => {
  let data = [];
  for (let key in nameConventions) {
    if (key !== target) {
      data.push(nameConventions[key]);
    }
  }
  return data;
};

export const trimAndLowerCase = (option) => trimValue(option).toLowerCase();

const isEmpty = (option) => trimValue(option).length === 0;

export const isNameConventionInvalid = (type, singularValue, pluralValue) => type === SINGULAR ? isInvalid(singularValue) : isInvalid(pluralValue);

export const isInvalid = (option) => isEmpty(option) || INTERNALLY_USED_WORDS.has(trimAndLowerCase(option));

export const generateLabelFromType = (type) => {
  return type.charAt(0).toUpperCase() + type.slice(1);
};

export const trimNameConventionsBeforeSaving = (nameConventions) => {
  const trimmedNameConventions = { ...nameConventions };
  for (let key in trimmedNameConventions) {
    if (key === SCORING_RUBRIC) {
      trimmedNameConventions[key] = trimmedNameConventions[key].map((option) => ({ ...option, name: trimValue(option.name) }));
    } else {
      trimmedNameConventions[key].alias = trimValue(trimmedNameConventions[key].alias);
      trimmedNameConventions[key].pluralFormAlias = trimValue(trimmedNameConventions[key].pluralFormAlias);
    }
  }
  return trimmedNameConventions;
};

////////// Default Name Convention Values ////////////////////////
const userGroup = {
  name: USER_GROUP,
  alias: 'User group',
  pluralFormAlias: 'User groups',
  description: 'This is a categorization to organize programs and line items that allows users to easily see and group programs and line items together.',
};
const programDepartment = {
  name: PROGRAM_DEPARTMENT,
  alias: 'Program department',
  pluralFormAlias: 'Program departments',
  description: 'This is the higher of two fields to organize the program inventory into an organizationally meaningful hierarchy.',
};
const programDivision = {
  name: PROGRAM_DIVISION,
  alias: 'Program division',
  pluralFormAlias: 'Program divisions',
  description: 'This is the lower of two fields to organize the program inventory into an organizationally meaningful hierarchy.',
};
const priority = {
  name: PRIORITY,
  alias: 'Priority',
  pluralFormAlias: 'Priorities',
  description: 'This is a scoring category or priority for every organization, usually reflected in a strategic plan or guiding document.',
};
const program = {
  name: PROGRAM,
  alias: 'Program',
  pluralFormAlias: 'Programs',
  description: 'This is a description of the service(s) offered by your organization; this is the foundational data layer upon which everything else, including budget requests, will be built.',
};

const request = {
  name: REQUEST,
  alias: 'Request',
  pluralFormAlias: 'Requests',
  description: 'This how your organization refers to budget requests, proposals, or submissions.',
};

export const NAME_CONVENTIONS = {
  [USER_GROUP]: {
    name: USER_GROUP,
    alias: userGroup.alias,
    pluralFormAlias: userGroup.pluralFormAlias
  },
  [PROGRAM_DEPARTMENT]: {
    name: PROGRAM_DEPARTMENT,
    alias: programDepartment.alias,
    pluralFormAlias: programDepartment.pluralFormAlias
  },
  [PROGRAM_DIVISION]: {
    name: PROGRAM_DIVISION,
    alias: programDivision.alias,
    pluralFormAlias: programDivision.pluralFormAlias
  },
  [PRIORITY]: {
    name: PRIORITY,
    alias: priority.alias,
    pluralFormAlias: priority.pluralFormAlias
  },
  [PROGRAM]: {
    name: PROGRAM,
    alias: program.alias,
    pluralFormAlias: program.pluralFormAlias
  },
  [REQUEST]: {
    name: REQUEST,
    alias: request.alias,
    pluralFormAlias: request.pluralFormAlias
  }
};

export const NAME_CONVENTION_INPUTS = [
  {
    NAME: USER_GROUP,
    TITLE: 'User group',
    DESCRIPTION: 'This is a categorization to organize programs and line items that allows users to easily see and group programs and line items together.'
  },
  {
    NAME: PROGRAM,
    TITLE: 'Program',
    DESCRIPTION: 'This is a description of the service(s) offered by your organization; this is the foundational data layer upon which everything else, including budget requests, will be built.'
  },
  {
    NAME: PROGRAM_DEPARTMENT,
    TITLE: 'Program department',
    DESCRIPTION: 'This is the higher of two fields to organize the program inventory into an organizationally meaningful hierarchy.'
  },
  {
    NAME: PROGRAM_DIVISION,
    TITLE: 'Program division',
    DESCRIPTION: 'This is the lower of two fields to organize the program inventory into an organizationally meaningful hierarchy.'
  },
  {
    NAME: REQUEST,
    TITLE: 'Request',
    DESCRIPTION: 'This how your organization refers to budget requests, proposals, or submissions.'
  },
  {
    NAME: PRIORITY,
    TITLE: 'Priority',
    DESCRIPTION: 'This is a scoring category or priority for every organization, usually reflected in a strategic plan or guiding document.'
  },
  {
    NAME: SCORING_RUBRIC,
    TITLE: 'Scoring rubric',
    DESCRIPTION: 'This is the language used for the priority rubric.'
  }
];

export const checkIfAnyNameConventionIsInvalid = (nameConventions) => {
  let isInvalid = false;

  for (const nameConvention of Object.values(nameConventions)) {
    if (Array.isArray(nameConvention)) {
      nameConvention.forEach(x => {
        if ((x.name || '').trim() === '' || INTERNALLY_USED_WORDS.has(trimAndLowerCase((x.name || '')))) {
          isInvalid = true;
        }
      });
    } else {
      if ((nameConvention.alias || '').trim() === '' || 
          (nameConvention.pluralFormAlias || '').trim() === '' || 
          INTERNALLY_USED_WORDS.has(trimAndLowerCase((nameConvention.alias || ''))) || 
          INTERNALLY_USED_WORDS.has(trimAndLowerCase((nameConvention.pluralFormAlias || '')))
      ) {
        isInvalid = true;
      }
    }
  }

  return isInvalid;
};
